import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  VStack,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import {
  HiOutlineCloudArrowUp,
  HiXMark,
  HiPlus,
  HiOutlineTrash,
  HiBars3,
} from "react-icons/hi2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { BASE_API_URL } from "../Components/config";
import { c } from "./classroomColors";
import { useClassroomToast } from "./classroomToast";
import { fileIcon, fileTypeLabel } from "./fileTypes";

const SEGMENTS_API = `${BASE_API_URL}/api/v1/course-segments`;

// Rich-text toolbar formats: subtitles (H2) & sub-sections (H3), bullet/numbered
// lists, basic formatting, links, and inline images.
const QUILL_TOOLBAR = [
  [{ header: [2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["blockquote", "link", "image"],
  ["clean"],
];
const QUILL_FORMATS = [
  "header", "bold", "italic", "underline",
  "list", "bullet", "blockquote", "link", "image",
];

const cloneTree = (tree) => JSON.parse(JSON.stringify(tree));

const CourseContentManager = ({ onClose, onSuccess, courseGuid, isDark = true }) => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null); // {mode:'existing', guid} | {mode:'new', parentGuid}
  const [draftName, setDraftName] = useState("");
  const [draftOverview, setDraftOverview] = useState("");
  const [newFiles, setNewFiles] = useState([]);

  // Drag-and-drop state
  const [dragGuid, setDragGuid] = useState(null);
  const [dragHasChildren, setDragHasChildren] = useState(false);
  const [activeZone, setActiveZone] = useState(null);

  const token = localStorage.getItem("accessToken");
  const toast = useClassroomToast(isDark);
  const authHeader = { Authorization: `Bearer ${token}` };

  const showError = useCallback(
    (msg) => toast({ title: msg, status: "error", duration: 3500, isClosable: true }),
    [toast]
  );

  // Rich editor: upload inline images to S3 and embed the URL (base64 embedding
  // is unreliable inside a modal and bloats the stored HTML).
  const quillRef = useRef(null);
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const editor = quillRef.current && quillRef.current.getEditor();
      if (!editor) return;
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      try {
        const fd = new FormData();
        fd.append("course_guid", courseGuid);
        fd.append("file", file);
        const res = await fetch(`${SEGMENTS_API}/content-image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) throw new Error();
        const { url } = await res.json();
        editor.insertEmbed(index, "image", url);
        editor.setSelection(index + 1);
      } catch {
        showError("Couldn't add image.");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseGuid, token]);

  const quillModules = useMemo(
    () => ({ toolbar: { container: QUILL_TOOLBAR, handlers: { image: imageHandler } } }),
    [imageHandler]
  );

  const fetchTree = useCallback(async () => {
    try {
      const res = await fetch(
        `${SEGMENTS_API}/by-course-guid/with-children/${courseGuid}`,
        { headers: authHeader }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTree(Array.isArray(data) ? data : []);
    } catch {
      showError("Couldn't load course content.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseGuid]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  const refresh = async () => {
    await fetchTree();
    if (typeof onSuccess === "function") onSuccess();
  };

  const findNode = (guid) => {
    for (const seg of tree) {
      if (seg.guid === guid) return { node: seg, parentGuid: null, isSub: false };
      for (const child of seg.child_segments || []) {
        if (child.guid === guid) return { node: child, parentGuid: seg.guid, isSub: true };
      }
    }
    return null;
  };

  const selectExisting = (node) => {
    setSelected({ mode: "existing", guid: node.guid });
    setDraftName(node.name || "");
    setDraftOverview(node.overview_html || "");
    setNewFiles([]);
  };

  const startNew = (parentGuid) => {
    setSelected({ mode: "new", parentGuid: parentGuid || null });
    setDraftName("");
    setDraftOverview("");
    setNewFiles([]);
  };

  const current = selected?.mode === "existing" ? findNode(selected.guid) : null;
  const isSub = selected?.mode === "new" ? Boolean(selected.parentGuid) : current?.isSub;

  // ---- Content mutations ---------------------------------------------------

  const createNode = async () => {
    if (!draftName.trim()) return false;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append(
        "course_segment_str",
        JSON.stringify({
          name: draftName,
          code: draftName,
          overview_html: draftOverview,
          course_guid: courseGuid,
          ...(selected.parentGuid ? { parent_course_segment_guid: selected.parentGuid } : {}),
        })
      );
      newFiles.forEach((f) => formData.append("attachments", f));

      const res = await fetch(`${SEGMENTS_API}/with-attachments`, {
        method: "POST",
        headers: authHeader,
        body: formData,
      });
      if (!res.ok) throw new Error(`Create failed (${res.status})`);
      const created = await res.json();
      await refresh();
      setSelected({ mode: "existing", guid: created.guid });
      setNewFiles([]);
      toast({ title: "Created", status: "success", duration: 2000, isClosable: true });
      return true;
    } catch (e) {
      console.error("Create segment failed", e);
      showError("Couldn't create this item.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // "Done" should not silently discard an in-progress new segment: commit it first.
  const handleDone = async () => {
    if (selected?.mode === "new") {
      if (draftName.trim()) {
        const ok = await createNode();
        if (!ok) return; // keep the modal open so the user can retry
      } else if (newFiles.length > 0) {
        showError("Add a title before saving this topic.");
        return;
      }
    }
    onClose();
  };

  const saveExisting = async () => {
    if (!current || !draftName.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${SEGMENTS_API}/${current.node.guid}`, {
        method: "PUT",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ name: draftName, code: draftName, overview_html: draftOverview }),
      });
      if (!res.ok) throw new Error();
      await refresh();
      toast({ title: "Saved", status: "success", duration: 2000, isClosable: true });
    } catch {
      showError("Couldn't save changes.");
    } finally {
      setBusy(false);
    }
  };

  const uploadToExisting = async (fileList) => {
    if (!current || fileList.length === 0) return;
    setBusy(true);
    try {
      const formData = new FormData();
      Array.from(fileList).forEach((f) => formData.append("attachments", f));
      const res = await fetch(`${SEGMENTS_API}/${current.node.guid}/attachments`, {
        method: "POST",
        headers: authHeader,
        body: formData,
      });
      if (!res.ok) throw new Error();
      await refresh();
    } catch {
      showError("Couldn't upload files.");
    } finally {
      setBusy(false);
    }
  };

  const deleteAttachment = async (attachmentGuid) => {
    setBusy(true);
    try {
      const res = await fetch(`${SEGMENTS_API}/attachments/${attachmentGuid}`, {
        method: "DELETE",
        headers: authHeader,
      });
      if (!res.ok) throw new Error();
      await refresh();
    } catch {
      showError("Couldn't remove file.");
    } finally {
      setBusy(false);
    }
  };

  const deleteNode = async () => {
    if (!current) return;
    const label = current.isSub ? "sub-topic" : "topic and all its sub-topics";
    if (!window.confirm(`Delete this ${label}? This can't be undone.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${SEGMENTS_API}/${current.node.guid}`, {
        method: "DELETE",
        headers: authHeader,
      });
      if (!res.ok) throw new Error();
      setSelected(null);
      await refresh();
      toast({ title: "Deleted", status: "success", duration: 2000, isClosable: true });
    } catch {
      showError("Couldn't delete this item.");
    } finally {
      setBusy(false);
    }
  };

  // ---- Drag and drop reordering -------------------------------------------

  const canDrop = (parentGuid) => {
    if (!dragGuid) return false;
    if (parentGuid === dragGuid) return false; // can't nest under itself
    if (parentGuid !== null && dragHasChildren) return false; // keep max depth of 2
    return true;
  };

  const persistOrder = async (nextTree) => {
    const items = [];
    nextTree.forEach((seg, i) => {
      items.push({ guid: seg.guid, parent_course_segment_guid: null, position: i });
      (seg.child_segments || []).forEach((child, j) => {
        items.push({ guid: child.guid, parent_course_segment_guid: seg.guid, position: j });
      });
    });

    setBusy(true);
    try {
      const res = await fetch(`${SEGMENTS_API}/reorder`, {
        method: "PUT",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ course_guid: courseGuid, items }),
      });
      if (!res.ok) throw new Error();
      if (typeof onSuccess === "function") onSuccess();
    } catch {
      showError("Couldn't reorder. Reverting.");
      await fetchTree();
    } finally {
      setBusy(false);
    }
  };

  const handleDrop = (parentGuid, index) => {
    const guid = dragGuid;
    if (!guid || !canDrop(parentGuid)) return;

    const next = cloneTree(tree);

    // Remove the dragged node from its current location.
    let moved = null;
    let from = null;
    const topIdx = next.findIndex((s) => s.guid === guid);
    if (topIdx >= 0) {
      moved = next.splice(topIdx, 1)[0];
      from = { parent: null, index: topIdx };
    } else {
      for (const seg of next) {
        const ci = (seg.child_segments || []).findIndex((c) => c.guid === guid);
        if (ci >= 0) {
          moved = seg.child_segments.splice(ci, 1)[0];
          from = { parent: seg.guid, index: ci };
          break;
        }
      }
    }
    if (!moved) return;

    // Removing an earlier item in the same list shifts the target index down by one.
    let insertIndex = index;
    if (from.parent === parentGuid && from.index < index) insertIndex = index - 1;

    // No-op drop (dropped back into the same slot).
    if (from.parent === parentGuid && insertIndex === from.index) return;

    if (parentGuid === null) {
      next.splice(insertIndex, 0, moved);
    } else {
      const target = next.find((s) => s.guid === parentGuid);
      if (!target) return;
      moved.child_segments = [];
      if (!target.child_segments) target.child_segments = [];
      target.child_segments.splice(insertIndex, 0, moved);
    }

    setTree(next);
    persistOrder(next);
  };

  // ---- Rendering -----------------------------------------------------------

  const DropZone = ({ parentGuid, index }) => {
    const key = `${parentGuid || "root"}:${index}`;
    const active = activeZone === key && canDrop(parentGuid);
    return (
      <Box
        h="8px"
        borderRadius="4px"
        transition="background 0.1s"
        bg={active ? c.accent : "transparent"}
        onDragOver={(e) => {
          if (canDrop(parentGuid)) {
            e.preventDefault();
            setActiveZone(key);
          }
        }}
        onDragLeave={() => setActiveZone((z) => (z === key ? null : z))}
        onDrop={(e) => {
          e.preventDefault();
          setActiveZone(null);
          handleDrop(parentGuid, index);
        }}
      />
    );
  };

  const DraggableRow = ({ node, indented }) => {
    const active = selected?.mode === "existing" && selected.guid === node.guid;
    return (
      <Flex
        align="center"
        gap="6px"
        padding="8px 8px"
        ml={indented ? "14px" : "0"}
        borderLeft={indented ? `2px solid ${c.border}` : "none"}
        borderRadius="8px"
        cursor="grab"
        background={active ? c.infoSoft : "transparent"}
        _hover={{ background: active ? c.infoSoft : c.hover }}
        transition="background 0.15s"
        opacity={dragGuid === node.guid ? 0.4 : 1}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", node.guid);
          setDragGuid(node.guid);
          setDragHasChildren((node.child_segments?.length || 0) > 0);
        }}
        onDragEnd={() => {
          setDragGuid(null);
          setDragHasChildren(false);
          setActiveZone(null);
        }}
        onClick={() => selectExisting(node)}
      >
        <Icon as={HiBars3} boxSize="14px" color={c.textMuted} flexShrink={0} />
        <Text
          fontSize={indented ? "12px" : "13px"}
          fontWeight={active ? "600" : "400"}
          color={active ? c.infoText : indented ? c.textSub : c.text}
          noOfLines={1}
        >
          {node.name}
        </Text>
      </Flex>
    );
  };

  const AddButton = ({ label, onClick, ml }) => (
    <Flex
      as="button"
      align="center"
      gap="6px"
      ml={ml}
      padding="6px 8px"
      borderRadius="8px"
      color={c.accent}
      _hover={{ background: c.hover }}
      onClick={onClick}
    >
      <Icon as={HiPlus} boxSize="13px" />
      <Text fontSize="12px" fontWeight="500">
        {label}
      </Text>
    </Flex>
  );

  const renderEditor = () => {
    if (!selected) {
      return (
        <Flex flex="1" align="center" justify="center" minH="360px">
          <Text fontSize="13px" color={c.textMuted} textAlign="center">
            Select an item on the left to edit it,
            <br />
            drag to reorder, or add something new.
          </Text>
        </Flex>
      );
    }

    const isNew = selected.mode === "new";
    const attachments = current?.node?.course_segment_attachments || [];

    return (
      <VStack flex="1" spacing="16px" align="stretch" minWidth="0">
        <Text fontSize="12px" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.05em">
          {isNew
            ? isSub
              ? "New sub-topic"
              : "New topic"
            : isSub
            ? "Editing sub-topic"
            : "Editing topic"}
        </Text>

        <FormControl isRequired>
          <FormLabel fontSize="13px" fontWeight="500" color={c.text} mb="6px">
            Title
          </FormLabel>
          <Input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder={isSub ? "e.g. Worked examples" : "e.g. Introduction to Kinematics"}
            fontSize="14px"
            borderColor={c.inputBorder}
            _hover={{ borderColor: c.textMuted }}
            _focus={{ borderColor: c.accent, boxShadow: `0 0 0 1px ${c.accent}` }}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="13px" fontWeight="500" color={c.text} mb="6px">
            Files &amp; media
          </FormLabel>

          {!isNew && attachments.length > 0 && (
            <VStack spacing="6px" align="stretch" mb="10px">
              {attachments.map((att) => (
                <Flex
                  key={att.guid}
                  align="center"
                  gap="10px"
                  px="10px"
                  py="8px"
                  border={`1px solid ${c.border}`}
                  borderRadius="8px"
                  bg={c.surface}
                >
                  <Icon as={fileIcon(att.filename)} boxSize="16px" color={c.info} flexShrink={0} />
                  <Text fontSize="12px" color={c.textSub} noOfLines={1} flex="1">
                    {att.filename}
                  </Text>
                  <Text fontSize="10px" color={c.textMuted} flexShrink={0}>
                    {fileTypeLabel(att.filename)}
                  </Text>
                  <Flex
                    as="button"
                    flexShrink={0}
                    p="2px"
                    borderRadius="4px"
                    _hover={{ background: c.hover }}
                    onClick={() => deleteAttachment(att.guid)}
                  >
                    <Icon as={HiXMark} boxSize="16px" color={c.textMuted} />
                  </Flex>
                </Flex>
              ))}
            </VStack>
          )}

          {isNew && newFiles.length > 0 && (
            <VStack spacing="6px" align="stretch" mb="10px">
              {newFiles.map((file, idx) => (
                <Flex
                  key={idx}
                  align="center"
                  gap="10px"
                  px="10px"
                  py="8px"
                  border={`1px solid ${c.border}`}
                  borderRadius="8px"
                  bg={c.surface}
                >
                  <Icon as={fileIcon(file.name)} boxSize="16px" color={c.info} flexShrink={0} />
                  <Text fontSize="12px" color={c.textSub} noOfLines={1} flex="1">
                    {file.name}
                  </Text>
                  <Text fontSize="10px" color={c.textMuted} flexShrink={0}>
                    {fileTypeLabel(file.name)}
                  </Text>
                  <Flex
                    as="button"
                    flexShrink={0}
                    p="2px"
                    borderRadius="4px"
                    _hover={{ background: c.hover }}
                    onClick={() => setNewFiles((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <Icon as={HiXMark} boxSize="16px" color={c.textMuted} />
                  </Flex>
                </Flex>
              ))}
            </VStack>
          )}

          <Flex
            direction="column"
            alignItems="center"
            justifyContent="center"
            h="90px"
            border="1px dashed" borderColor={c.textMuted}
            borderRadius="10px"
            bg={c.surfaceSubtle}
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ background: c.hover, borderColor: c.accent }}
            onClick={() => document.getElementById("cms-media-upload").click()}
          >
            <Icon as={HiOutlineCloudArrowUp} boxSize="20px" color={c.accent} mb="4px" />
            <Text fontSize="12px" fontWeight="500" color={c.text}>
              {isNew ? "Click to attach" : "Click to upload"}
            </Text>
            <Text fontSize="11px" color={c.textMuted}>
              Images, video, PDF, Word, Excel, ZIP…
            </Text>
          </Flex>
          <Input
            id="cms-media-upload"
            type="file"
            multiple
            display="none"
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.csv,.txt,.rtf"
            onChange={(e) => {
              if (isNew) {
                setNewFiles((prev) => [...prev, ...Array.from(e.target.files)]);
              } else {
                uploadToExisting(e.target.files);
              }
              e.target.value = "";
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="13px" fontWeight="500" color={c.text} mb="6px">
            Content
          </FormLabel>
          <Box
            className="cms-quill"
            sx={{
              "& .ql-toolbar": {
                borderTopRadius: "8px",
                borderColor: c.inputBorder,
                background: c.surfaceSubtle,
              },
              "& .ql-container": {
                borderBottomRadius: "8px",
                borderColor: c.inputBorder,
                fontSize: "14px",
                fontFamily: "inherit",
              },
              "& .ql-editor": { minHeight: "220px", color: c.text },
              "& .ql-editor.ql-blank::before": { color: c.textMuted, fontStyle: "normal" },
              "& .ql-stroke": { stroke: c.textSub },
              "& .ql-fill": { fill: c.textSub },
              "& .ql-picker, & .ql-picker-label, & .ql-picker-options": { color: c.textSub },
            }}
          >
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={draftOverview}
              onChange={(html) => setDraftOverview(html)}
              modules={quillModules}
              formats={QUILL_FORMATS}
              placeholder="Add subtitles, sub-sections, bullet points and images…"
            />
          </Box>
        </FormControl>

        <Flex justify="space-between" align="center" pt="8px">
          {!isNew ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Icon as={HiOutlineTrash} boxSize="16px" />}
              color="#E53E3E"
              fontSize="13px"
              fontWeight="500"
              _hover={{ bg: c.hover }}
              onClick={deleteNode}
              isDisabled={busy}
            >
              Delete
            </Button>
          ) : (
            <Box />
          )}

          <Button
            bg={c.text}
            color={c.surface}
            size="sm"
            fontSize="13px"
            fontWeight="500"
            px="18px"
            _hover={{ bg: c.hover }}
            onClick={isNew ? createNode : saveExisting}
            isLoading={busy}
            loadingText={isNew ? "Creating" : "Saving"}
            isDisabled={!draftName.trim()}
          >
            {isNew ? "Create" : "Save changes"}
          </Button>
        </Flex>
      </VStack>
    );
  };

  return (
    <Flex gap="20px" align="stretch" minH="420px">
      {/* LEFT: draggable structure */}
      <Box width="240px" flexShrink={0} borderRight={`1px solid ${c.border}`} pr="16px" maxH="460px" overflowY="auto">
        <Text
          fontSize="11px"
          fontWeight="600"
          color={c.textMuted}
          textTransform="uppercase"
          letterSpacing="0.05em"
          mb="4px"
        >
          Structure
        </Text>
        <Text fontSize="11px" color={c.textMuted} mb="8px">
          Drag to reorder or re-nest
        </Text>

        {loading ? (
          <Flex justify="center" py="24px">
            <Spinner size="sm" color={c.accent} />
          </Flex>
        ) : (
          <Box>
            <DropZone parentGuid={null} index={0} />
            {tree.map((seg, si) => (
              <Box key={seg.guid}>
                <DraggableRow node={seg} indented={false} />
                <Box>
                  <DropZone parentGuid={seg.guid} index={0} />
                  {(seg.child_segments || []).map((child, ci) => (
                    <Box key={child.guid}>
                      <DraggableRow node={child} indented />
                      <DropZone parentGuid={seg.guid} index={ci + 1} />
                    </Box>
                  ))}
                </Box>
                <AddButton label="Add sub-topic" ml="14px" onClick={() => startNew(seg.guid)} />
                <DropZone parentGuid={null} index={si + 1} />
              </Box>
            ))}
            {tree.length === 0 && (
              <Text fontSize="12px" color={c.textMuted} py="8px">
                No segments yet.
              </Text>
            )}
            <Box pt="6px" borderTop={tree.length ? `1px solid var(--cl-border, #e4e4e4)` : "none"} mt="6px">
              <AddButton label="Add topic" onClick={() => startNew(null)} />
            </Box>
          </Box>
        )}
      </Box>

      {/* RIGHT: editor */}
      <Flex flex="1" direction="column" minWidth="0">
        <Box flex="1" maxH="460px" overflowY="auto" pr="4px">
          {renderEditor()}
        </Box>
        <Flex justify="flex-end" pt="12px" mt="12px" borderTop={`1px solid ${c.border}`}>
          <Button
            variant="ghost"
            size="sm"
            fontSize="13px"
            fontWeight="500"
            color={c.textSub}
            onClick={handleDone}
            isLoading={busy}
          >
            Done
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CourseContentManager;
